// src/pages/BlogPost.js
import React, { useEffect } from 'react';
import { getPostBySlug } from '../posts';

const sans = "'DM Sans', system-ui, sans-serif";
const serif = "'DM Serif Display', Georgia, serif";

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function renderContent(content) {
  const lines = content.trim().split('\n').filter(l => l.trim() !== '');
  const body = lines.slice(1); // skip duplicate title line

  return body.map((line, i) => {
    const trimmed = line.trim();
    const isSubheading =
      trimmed.length < 90 &&
      !trimmed.endsWith('.') &&
      !trimmed.endsWith('?') &&
      !trimmed.endsWith('🌿') &&
      i > 0;

    if (isSubheading) {
      return (
        <h2 key={i} style={{
          fontFamily: serif,
          fontSize: '22px',
          fontWeight: '400',
          color: '#1a1a18',
          marginTop: '40px',
          marginBottom: '14px',
          lineHeight: '1.3',
        }}>
          {trimmed}
        </h2>
      );
    }
    return (
      <p key={i} style={{
        fontFamily: sans,
        fontSize: '16px',
        color: '#4a6070',
        lineHeight: '1.8',
        marginBottom: '20px',
        fontWeight: '300',
      }}>
        {trimmed}
      </p>
    );
  });
}

export default function BlogPost({ slug, onBack, onBlog, onStartTracking }) {
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) document.title = `${post.title} — ApptNote`;
    window.scrollTo(0, 0);
  }, [slug, post]);

  if (!post) {
    return (
      <div style={styles.page}>
        <div style={styles.notFound}>
          <p style={{ fontFamily: sans, color: '#4a6070' }}>Article not found.</p>
          <button style={styles.blogLink} onClick={onBlog}>← Back to journal</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.nav}>
          <button style={styles.back} onClick={onBlog}>← All articles</button>
          <div style={styles.logo}>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
        </div>

        <div style={styles.meta}>
          <span style={styles.metaDate}>{formatDate(post.date)}</span>
          <span style={styles.metaDot} />
          <span style={styles.metaRead}>{post.readTime}</span>
        </div>

        <h1 style={styles.title}>{post.title}</h1>

        <hr style={styles.divider} />

        <div style={styles.body}>
          {renderContent(post.content)}
        </div>

        <p style={styles.articleDisclaimer}>
          This article is general information, not medical advice. Talk to your doctor
          about your own symptoms and treatment.
        </p>

        <div style={styles.cta}>
          <p style={styles.ctaLabel}>ApptNote</p>
          <p style={styles.ctaTitle}>Track your symptoms.<br />Walk in prepared.</p>
          <p style={styles.ctaText}>
            ApptNote keeps your daily symptom logs in one place and uses AI to pull them into a
            clear summary, in your own words, to bring to your appointment.
          </p>
          <button style={styles.ctaButton} onClick={onStartTracking}>
            Start tracking free →
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#FAF7F2',
    fontFamily: sans,
  },
  inner: {
    padding: '24px 24px 80px',
    maxWidth: '680px',
    margin: '0 auto',
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '48px',
  },
  back: {
    fontFamily: sans, fontSize: '14px', color: '#6c8396', fontWeight: '400',
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  },
  logo: { fontFamily: sans, fontSize: '20px', fontWeight: '700' },
  logoAppt: { color: '#2C2C2A' },
  logoNote: { color: '#D4A5A5' },
  meta: {
    display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px',
  },
  metaDate: { fontFamily: sans, fontSize: '13px', color: '#9aabb8', fontWeight: '300' },
  metaDot: { width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#C4B8A8' },
  metaRead: { fontFamily: sans, fontSize: '13px', color: '#9aabb8', fontWeight: '300' },
  title: {
    fontFamily: serif, fontSize: '36px', fontWeight: '400',
    color: '#1a1a18', lineHeight: '1.2', marginBottom: '32px',
    letterSpacing: '-0.01em',
  },
  divider: {
    border: 'none', borderTop: '1px solid rgba(44,44,42,0.08)', marginBottom: '40px',
  },
  body: { paddingBottom: '48px' },
  cta: {
    backgroundColor: 'white',
    border: '1px solid rgba(212,165,165,0.2)',
    borderLeft: '3px solid #D4A5A5',
    borderRadius: '14px',
    padding: '32px 28px',
    marginBottom: '40px',
  },
  ctaLabel: {
    fontFamily: sans, fontSize: '11px', fontWeight: '600',
    letterSpacing: '0.12em', textTransform: 'uppercase',
    color: '#D4A5A5', marginBottom: '12px',
  },
  articleDisclaimer: {
    fontFamily: sans,
    fontSize: '13px',
    color: '#6c8396',
    lineHeight: 1.6,
    fontStyle: 'italic',
    margin: '32px 0 0',
  },
  ctaTitle: {
    fontFamily: serif, fontSize: '24px', fontWeight: '400',
    color: '#1a1a18', marginBottom: '12px', lineHeight: '1.3',
  },
  ctaText: {
    fontFamily: sans, fontSize: '15px', color: '#4a6070',
    lineHeight: '1.65', marginBottom: '24px', fontWeight: '300',
  },
  ctaButton: {
    display: 'inline-block', backgroundColor: '#2C2C2A', color: 'white',
    padding: '13px 28px', borderRadius: '10px', fontWeight: '600',
    fontSize: '15px', border: 'none', cursor: 'pointer', fontFamily: sans,
  },
  notFound: { padding: '80px 24px', textAlign: 'center' },
  blogLink: {
    fontFamily: sans, background: 'none', border: 'none',
    color: '#7A9E87', fontWeight: '500', cursor: 'pointer',
    fontSize: '15px', marginTop: '16px',
  },
};
